import React, { useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Loader2,
  Megaphone,
  Radio,
  RefreshCw,
} from "lucide-react";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import LiveContestSidebar, { WISHAUR_RED } from "components/LiveContestSidebar";
import ContestProductControls from "components/ContestProductControls";
import { AnimatedLiveCounter, LiveEntriesDisplay } from "components/AnimatedLiveCounter";
import LiveDashboardStats from "components/LiveDashboardStats";

/**
 * Phone-first live leaderboard layout (separate from desktop LiveQuizView).
 */
const LiveMobileView = ({
  product,
  selectedWish,
  history,
  uniqueUserCount = 0,
  historyLoading = false,
  onClose,
  onRefresh,
  refreshing = false,
  refreshTrigger = 0,
  realtimeConnected = false,
  dashboardStats,
  dashboardStatsLoading = false,
  onRefreshDashboardStats,
  dashboardStatsRefreshing = false,
  productActive = true,
  onToggleProductActive,
  togglingProductStatus = false,
  productPlayable = true,
  onToggleProductPlayable,
  togglingPlayableStatus = false,
  onConfirmAnnounceResult,
  announcingResult = false,
  canAnnounceResult = false,
}) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const [announceDialogOpen, setAnnounceDialogOpen] = useState(false);

  const contestName = product?.productName || "Contest";
  const contestStatus = selectedWish?.status || "LIVE";
  const fillWish = selectedWish?.fillWish ?? 0;
  const totalWish = product?.totalWish ? Number(product.totalWish) : null;
  const statusUpper = (contestStatus || "").toUpperCase();
  const showAnnounce =
    canAnnounceResult &&
    onConfirmAnnounceResult &&
    (statusUpper === "ONGOING" ||
      statusUpper === "PENDINGRESULT" ||
      statusUpper === "PENDINGRESULT_EVENT");

  const handleConfirmAnnounce = async () => {
    if (!onConfirmAnnounceResult) return;
    try {
      await onConfirmAnnounceResult();
      setAnnounceDialogOpen(false);
    } catch {
      // Parent shows toast; keep dialog open for retry.
    }
  };

  return (
    <div className="min-h-[100dvh] w-full bg-[#1a1a1a] flex justify-center overflow-x-hidden">
      {/* Phone frame — always mobile width even on desktop */}
      <div className="flex flex-col w-full max-w-[390px] min-h-[100dvh] h-[100dvh] bg-[#F5F5F5] shadow-2xl overflow-hidden relative">
      <header
        className="sticky top-0 z-20 shrink-0 px-2 py-2 flex items-center justify-between gap-2"
        style={{ backgroundColor: WISHAUR_RED }}
      >
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          {onClose && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 px-2 text-white hover:bg-white/20 hover:text-white shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-sm">W</span>
          </div>
          <div className="min-w-0">
            <h1 className="text-white font-bold text-base tracking-tight truncate leading-tight">
              {contestName}
            </h1>
            <p className="text-white/85 text-[11px] truncate">
              Live mobile · {contestStatus}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {onRefresh && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing}
              className="h-8 px-2 text-white hover:bg-white/20 hover:text-white"
              title="Refresh contest data"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          )}
          {realtimeConnected && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-white/90 px-1.5 py-0.5 rounded-full bg-white/15"
              title="Realtime updates connected"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
              Live
            </span>
          )}
        </div>
      </header>

      <div className="shrink-0 px-2 pt-2 space-y-2">
        <Card className="border-0 shadow-sm rounded-xl overflow-hidden">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white"
                style={{ backgroundColor: WISHAUR_RED }}
              >
                <Radio className="w-3 h-3" />
                {contestStatus}
              </span>
            </div>
            <div className="space-y-2">
              <div className="bg-gray-50 rounded-lg p-2.5 min-w-0 overflow-hidden">
                <LiveEntriesDisplay
                  entries={fillWish}
                  total={totalWish}
                  live={realtimeConnected}
                  accentColor={WISHAUR_RED}
                  compact
                />
              </div>
              <div className="bg-gray-50 rounded-lg px-2.5 py-2 flex items-baseline justify-between gap-2">
                <p className="text-[10px] font-semibold text-gray-600 shrink-0">unique users</p>
                <p className="text-lg font-bold text-gray-900 tabular-nums leading-none">
                  <AnimatedLiveCounter
                    value={uniqueUserCount}
                    size="md"
                    className="!text-lg"
                  />
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setMoreOpen((open) => !open)}
              className="mt-2 w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              <span>More controls &amp; stats</span>
              {moreOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>

            {moreOpen && (
              <div className="mt-2 space-y-3 border-t border-gray-100 pt-3">
                {(onToggleProductActive || onToggleProductPlayable) && (
                  <ContestProductControls
                    productActive={productActive}
                    onToggleProductActive={onToggleProductActive}
                    togglingProductStatus={togglingProductStatus}
                    productPlayable={productPlayable}
                    onToggleProductPlayable={onToggleProductPlayable}
                    togglingPlayableStatus={togglingPlayableStatus}
                    accentColor={WISHAUR_RED}
                  />
                )}

                {showAnnounce && (
                  <Button
                    type="button"
                    className="w-full h-9 text-white font-semibold text-xs"
                    style={{ backgroundColor: WISHAUR_RED }}
                    disabled={announcingResult}
                    onClick={() => setAnnounceDialogOpen(true)}
                  >
                    {announcingResult ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Announcing…
                      </>
                    ) : (
                      <>
                        <Megaphone className="h-3.5 w-3.5 mr-1.5" />
                        {statusUpper === "ONGOING"
                          ? "Close & announce result"
                          : "Announce result"}
                      </>
                    )}
                  </Button>
                )}

                <div className="min-h-[16rem]">
                  <LiveDashboardStats
                    stats={dashboardStats}
                    loading={dashboardStatsLoading}
                    onRefresh={onRefreshDashboardStats}
                    refreshing={dashboardStatsRefreshing}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex-1 min-h-0 px-2 pb-2 pt-2 flex flex-col overflow-hidden">
        <LiveContestSidebar
          product={product}
          selectedWish={selectedWish}
          fallbackHistory={history}
          fallbackLoading={historyLoading}
          refreshTrigger={refreshTrigger}
          realtimeConnected={realtimeConnected}
          embedded
          className="!min-h-0 h-full"
        />
      </div>

      <Dialog open={announceDialogOpen} onOpenChange={setAnnounceDialogOpen}>
        <DialogContent className="max-w-[min(340px,calc(100vw-2rem))]">
          <DialogHeader>
            <DialogTitle>
              {statusUpper === "ONGOING" ? "Close & announce result?" : "Announce result?"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-gray-600">
                {statusUpper === "ONGOING" ? (
                  <>
                    <p>
                      This will close the round (PENDINGRESULT), distribute prizes to winners,
                      and mark the round as <strong>PROCESSED</strong>.
                    </p>
                    {selectedWish?.isCurrentRound && (
                      <p>
                        This is the current round — a new ONGOING round will be created and
                        the product will stop accepting entries until re-enabled.
                      </p>
                    )}
                  </>
                ) : (
                  <p>
                    This will immediately process payouts and mark the round as{" "}
                    <strong>PROCESSED</strong> (skips the 10-minute cron wait).
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAnnounceDialogOpen(false)}
              disabled={announcingResult}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmAnnounce}
              disabled={announcingResult}
              style={{ backgroundColor: WISHAUR_RED }}
              className="text-white"
            >
              {announcingResult
                ? "Announcing…"
                : statusUpper === "ONGOING"
                  ? "Close & announce"
                  : "Announce result"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default LiveMobileView;
